import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Collapse,
  Tag,
  Space,
  Empty,
  Form,
  Input,
  Button,
  Row,
  Col,
  Divider,
  Alert,
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import RequestViewer from '../../components/RequestViewer';
import { useAutoRequest } from '../../hooks/useAutoRequest';
import JsonViewer from '../../components/common/JsonViewer';
import { parseIds } from '../../utils/parseIds';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

/**
 * Página de monitoramento de auto requests (CW2).
 * 
 * Exibe todos os auto requests que estão sendo monitorados,
 * incluindo os requests filhas gerados por cada CNJ.
 */
const AutoRequestMonitoringContent: React.FC = () => {
  const { cnjs, createAutoRequest, setCnjs, autoRequestOptions } = useAutoRequest();
  const [autoRequestValue, setAutoRequestValue] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [form] = Form.useForm();

  /**
   * Cria todos os auto requests quando os CNJs são definidos.
   */
  useEffect(() => {
    const createAllAutoRequests = async () => {
      if (cnjs.length === 0) return;

      const newAutoRequestValue: Record<string, any> = { ...autoRequestValue };
      const newLoading: Record<string, boolean> = { ...loading };

      for (let i = 0; i < cnjs.length; i += 1) {
        const cnj = cnjs[i];
        if (!newAutoRequestValue[cnj] && !newLoading[cnj]) {
          try {
            newLoading[cnj] = true;
            setLoading({ ...newLoading });
            // Usa as opções do contexto (makeDownload e forced)
            const result = await createAutoRequest(cnj, autoRequestOptions);
            newAutoRequestValue[cnj] = result;
            setAutoRequestValue({ ...newAutoRequestValue });
        } catch (error) {
            console.error(`Erro ao criar auto request para CNJ ${cnj}:`, error);
          } finally {
            newLoading[cnj] = false;
            setLoading({ ...newLoading });
          }
        }
      }
    };

    createAllAutoRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnjs]);

  /**
   * Handler para adicionar novos CNJs para monitoramento.
   */
  const handleSubmit = async (values: { cnjs: string }) => {
    const cnjsText = values.cnjs || '';
    if (cnjsText.trim()) {
      const cnjList = parseIds(cnjsText);

      if (cnjList.length > 0) {
        setCnjs(cnjList);
      } else {
        setCnjs([]);
      }
    }
  };

  if (cnjs.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>
              <FileTextOutlined /> Monitoramento de Auto Requests
            </Title>
            <Paragraph>
              Digite os CNJs que deseja monitorar. Você pode usar dois formatos: um CNJ por linha ou um array JSON.
            </Paragraph>
          </div>

          <Alert
            message="Formatos Aceitos"
            description={
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <Text strong>Formato 1 - Múltiplas linhas:</Text>
                    <TextArea
                      value="5007878-28.2018.4.04.7204\n5010205-96.2016.4.04.7209"
                      readOnly
                      rows={2}
                      style={{ fontFamily: "monospace" }}
                    />
                  </Space>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <Text strong>Formato 2 - Array JSON:</Text>
                    <TextArea
                      value={JSON.stringify(["5007878-28.2018.4.04.7204", "5010205-96.2016.4.04.7209"], null, 2)}
                      readOnly
                      rows={3}
                      style={{ fontFamily: "monospace" }}
                    />
                  </Space>
                </Col>
              </Row>
            }
            type="info"
            showIcon
          />

          <Form form={form} onFinish={handleSubmit} layout="vertical">
            <Form.Item
              name="cnjs"
              label="Lista de CNJs"
              rules={[
                { required: true, message: 'Por favor, insira pelo menos um CNJ!' },
                {
                  validator: (_, value) => {
                    if (!value || value.trim().length === 0) {
                      return Promise.reject(new Error("Por favor, insira pelo menos um CNJ"));
                    }
                    const cnjList = parseIds(value);
                    if (cnjList.length === 0) {
                      return Promise.reject(new Error("Por favor, insira pelo menos um CNJ válido"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TextArea
                rows={10}
                placeholder='Digite os CNJs (um por linha) ou um array JSON: ["cnj1", "cnj2", ...]'
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" block>
                Monitorar
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2}>
            <FileTextOutlined /> Auto Requests Sendo Monitorados
          </Title>
          <Paragraph>
            <Text strong>CNJs: </Text>
            {cnjs.map((cnj, idx) => (
              <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
                <Text copyable style={{ color: 'inherit' }}>{cnj}</Text>
              </Tag>
            ))}
          </Paragraph>
        </div>

        <Divider />

        <Collapse>
          {cnjs.map((cnj, idx) => {
            const autoRequest = autoRequestValue[cnj];
            const isLoading = loading[cnj];

            return (
              <Panel
                key={idx}
                header={
                  <Space>
                    <Text strong>CNJ: </Text>
                    <Text copyable code>{cnj}</Text>
                    {autoRequest?.data?.id && (
                      <Tag color="green">
                        Auto Request ID: <Text copyable style={{ color: 'inherit' }}>{autoRequest.data.id}</Text>
                      </Tag>
                    )}
                    {isLoading && <Tag color="processing">Carregando...</Tag>}
                  </Space>
                }
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {isLoading && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Text type="secondary">Criando auto request...</Text>
                    </div>
                  )}

                  {autoRequest && (
                    <>
                      <div>
                        <Text strong>Resposta do Auto Request:</Text>
                        <JsonViewer src={autoRequest} name="auto-request" collapsed={true} />
                      </div>

                      {autoRequest.data?.requests && autoRequest.data.requests.length > 0 && (
                        <div>
                          <Title level={4}>Requisições Filhas:</Title>
                          <Row gutter={[16, 16]}>
                            {autoRequest.data.requests.map((request: any, reqIdx: number) => (
                              <Col xs={24} sm={24} md={12} lg={12} key={reqIdx}>
                                <RequestViewer requestId={request.id} />
                              </Col>
            ))}
                          </Row>
                        </div>
                      )}
                    </>
                  )}

                  {!autoRequest && !isLoading && (
                    <Empty
                      description="Erro ao criar auto request"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </Space>
              </Panel>
            );
          })}
        </Collapse>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página AutoRequestMonitoring.
 */
const AutoRequestMonitoring: React.FC = () => {
  return (
    <AutoRequestMonitoringContent />
  );
};

export default AutoRequestMonitoring;
